import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Phone, Mail } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    // Basic form validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return false;
    }
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    
    if (!formData.message.trim()) {
      toast.error('Please enter a message');
      return false;
    }
    
    return true;
  };



  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting) return;

    setIsSubmitting(true);

    // Configure your EmailJS service details
    emailjs
      .sendForm('service_8bfkycn', 'template_h6tt4g8', form.current, 'HgcT4VNgnV8_fYYod')
      .then(
        (result) => {
          // Success toast
          toast.success('Thank you for your message! We will get back to you soon.', {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          
          // Reset form
          setFormData({ name: '', email: '', message: '' });
          
          // Log success for debugging
          console.log('SUCCESS!', result.text);
        },
        (error) => {
          // Error toast with more detailed message
          toast.error(`Failed to send email. ${error.text}`, {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          console.error('Email send failed:', error.text);
        }
      )
      .finally(() => {
        // Always reset submitting state
        setIsSubmitting(false);
      });
  };

  return (
    <div className="sm:mt-8  md:pt-6 lg:pt-8 pt-10">
    <div className="min-h-t-screen bg-gray-900 py-16 px-4 flex items-center">
      <div className="container mx-auto grid md:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-6"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-cyan-500">
            Contact Beta Pixel Studio
          </h2>
          <p className="text-gray-300 text-lg">
            Ready to transform your digital vision? Get in touch with us today! We're excited to hear about your project and provide a tailored solution.
          </p>

          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <MapPin className="text-cyan-500" size={30} />
              <span className="text-gray-300">Remote First - Global Services</span>
            </div>
            {/* <div className="flex items-center space-x-4">
              <Phone className="text-cyan-500" size={30} />
              <span className="text-gray-300">+92 3401743758</span>
            </div> */}
            <div className="flex items-center space-x-4">
              <Mail className="text-cyan-500" size={30} />
              <span className="text-gray-300">info@betapixelstudio.com</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-gray-800/50 p-8 rounded-xl border border-purple-500/30"
        >
          <form ref={form} onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="text-gray-300 block mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Your Name"
              />
            </div>
            <div>
              <label htmlFor="email" className="text-gray-300 block mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="your@email.com"
              />
            </div>
            <div>
              <label htmlFor="message" className="text-gray-300 block mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                placeholder="Tell us about your project..."
              ></textarea>
            </div>
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`w-full bg-gradient-to-r from-purple-600 to-cyan-500 text-white py-3 rounded-lg hover:shadow-xl hover:shadow-purple-500/30 transition-all flex items-center justify-center space-x-2 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Send size={20} />
              <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
            </motion.button>
          </form>
        </motion.div>
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer 
        theme="dark"
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
    </div>

  );
};

export default Contact;